import * as dateMath from 'date-arithmetic';

export type Period = { start:Date, end:Date }
export type GraphPeriod = { startMonthYear: Date, months: number }

export function getMonthStart(dt:Date)
{
    return new Date(dt.getFullYear(), dt.getMonth(), 1);
}

export function periodsOverlap(graphPeriod:Period, period:Period)
{
    const detached = period.end < graphPeriod.start || period.start >= graphPeriod.end;
    return !detached;
}

export function periodOverlapPeriods(period:Period, periodList:Period[]){
    return periodList.find(p => periodsOverlap(p, period)) !== undefined;
}

export function getPeriodCanvasCoords(graphStartMonthYear:Date, period:Period, graphWidthPx:number)
{
    const result = getCanvasSpec({ startMonthYear:graphStartMonthYear, months:12}, graphWidthPx, 5)
        .getEventSpec(period);

    return result===null 
        ? null
        : {
            leftOffsetPx:result.eventLeftPx,
            widthPx:result.eventWidthPx
        };
}

export function getCanvasSpec(graphPeriod: GraphPeriod, graphWidthPx: number, eventMinWidthPx: number) {
    if (!Number.isInteger(graphPeriod.months) || graphPeriod.months < 1)
        throw new Error('invalid months');

    const pxPerMonth = graphWidthPx / graphPeriod.months;

    function getGraphBounds() {
        const start = new Date(graphPeriod.startMonthYear.getFullYear(), graphPeriod.startMonthYear.getMonth(), 1);
        const end = dateMath.add(
            dateMath.add(start, graphPeriod.months, 'month'),
            -1,
            'milliseconds'
        );
        return { start, end };
    }

    function dateIsInPeriod(event: Date, period: Period) {
        return event >= period.start && event <= period.end;
    }

    function eventIsInPeriod(event: Period, period: Period) {
        return event.start < period.end && event.end > period.start;
    }

    function getMonthStart(yearMonth: Date) {
        return new Date(yearMonth.getFullYear(), yearMonth.getMonth(), 1)
    }

    function getMonthEnd(yearMonth: Date) {
        return dateMath.add(
            dateMath.add(getMonthStart(yearMonth), 1, 'month'),
            -1,
            'milliseconds');
    }

    function getMonthSpec(yearMonth: Date) {
        const start = getMonthStart(yearMonth);
        const end = getMonthEnd(yearMonth);
        return {
            start,
            end,
            period: { start, end },
            year: start.getFullYear(),
            zeroIndexMonth: start.getMonth(),
            milliseconds: end.getTime() - start.getTime()
        };
    }

    return {

        getEventSpec(eventPeriod: Period):{eventLeftPx:number, eventWidthPx:number} | null {
            const graphBounds = getGraphBounds();
            if (!eventIsInPeriod(eventPeriod, graphBounds))
                return null;

            const safeEventStart = eventPeriod.start < graphBounds.start
                ? graphBounds.start
                : eventPeriod.start;

            const safeEventEnd = eventPeriod.end > graphBounds.end
                ? graphBounds.end
                : eventPeriod.end;

            const safeEventPeriod = { start: safeEventStart, end: safeEventEnd };

            let eventLeftPx = 0;
            if (safeEventPeriod.start > graphBounds.start) {
                for (let i = 0; i < graphPeriod.months; i++) {
                    const monthStart = dateMath.add(graphBounds.start, i, 'month');
                    const monthSpec = getMonthSpec(monthStart);
                    if (dateIsInPeriod(safeEventPeriod.start, monthSpec.period)) {
                        const monthOffsetMs = safeEventPeriod.start.getTime() - monthStart.getTime();
                        const monthOffsetPx = monthOffsetMs / monthSpec.milliseconds * pxPerMonth;
                        eventLeftPx = (i * pxPerMonth) + monthOffsetPx;
                        break;
                    }
                }
            }

            let currentMonthSpec = getMonthSpec(safeEventStart);
            let widthPx = 0;
            while (eventIsInPeriod(currentMonthSpec.period, safeEventPeriod)) {
                const startMs = Math.max(currentMonthSpec.start.getTime(), safeEventPeriod.start.getTime());
                const endMs = Math.min(currentMonthSpec.end.getTime(), safeEventPeriod.end.getTime());
                const diffMs = endMs - startMs;
                const diffPx = diffMs / currentMonthSpec.milliseconds * pxPerMonth;
                widthPx += diffPx;
                currentMonthSpec = getMonthSpec(dateMath.add(currentMonthSpec.start, 1, 'month'));
            }

            // Adjust for very small event;
            widthPx = Math.max(eventMinWidthPx, widthPx);
            // But adjust again if rendering off graph
            if (eventLeftPx + eventMinWidthPx > graphWidthPx)
                widthPx = graphWidthPx - eventLeftPx;

            const returnVal = {
                eventLeftPx,
                eventWidthPx: widthPx
            };
            return returnVal;
        }
    }
}